import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlay,
  faCirclePause,
  faCircleStop,
  faPen,
  faTriangleExclamation,
  faCalendarCheck,
  faRotate,
  faLock,
  faEnvelope,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import './StudentSubscription.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const STATES = [
  { key: 'active', label: 'Active', icon: faCirclePlay, tone: 'is-good' },
  { key: 'paused', label: 'Paused', icon: faCirclePause, tone: 'is-warn' },
  { key: 'stopped', label: 'Stopped', icon: faCircleStop, tone: 'is-off' },
  // reserved, not paid yet (only shown while the student is pending)
  { key: 'pending', label: 'Pending', icon: faLock, tone: 'is-warn' },
];
const PAY_LABEL = { whish: 'Whish', omt: 'OMT', bob: 'BOB Finance', wu: 'Western Union', card: 'credit card (payment link)' };
const toneOf = (s) => STATES.find((x) => x.key === s)?.tone || 'is-good';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const niceDay = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

/* Subscription card on the student Overview: Active / Paused / Stopped (+ Pending: reserved, not paid yet).
   Pausing or stopping can keep the weekly slots reserved or free them for other students;
   freed slots are remembered and can be given back when the student returns. */
const StudentSubscription = ({ studentId, onStatus, onOpenSchedule }) => {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null); // { status, pauseUntil, note, slots, restoreSlots }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [done, setDone] = useState('');
  // undo an enroll made from the family's link (a test / a mistake)
  const [undo, setUndo] = useState(null); // { result } while the panel is open
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState('');
  const doUndo = async () => {
    setUndoBusy(true);
    setUndoError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/studentprofile/undolinkenroll/${studentId}`,
        { result: undo.result === '' ? null : Number(undo.result) },
        auth()
      );
      // this pending student is gone: open the demo student again
      window.location.href = res.data?.demoId ? `/Admin/studentprofile/${res.data.demoId}` : '/Admin/users';
    } catch (err) {
      setUndoError(err.response?.data?.error || 'Couldn’t undo it.');
      setUndoBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API}/api/admin/studentprofile/subscription/${studentId}`, auth());
      setSub(res.data);
      onStatus?.(res.data.status);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'We couldn’t load the subscription.');
    } finally {
      setLoading(false);
    }
  }, [studentId, onStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (status) => {
    setSaveError('');
    setDone('');
    setDraft({
      status,
      pauseUntil: status === 'paused' || status === 'pending' ? sub.pauseUntil || '' : '',
      note: status === sub.status ? sub.note : '',
      slots: 'keep',
      restoreSlots: true,
      demoBonus: sub.demoBonus ? sub.demoBonus.give : null, // pending -> active: package bonus for the demo teacher?
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await axios.put(
        `${API}/api/admin/studentprofile/subscription/${studentId}`,
        {
          status: draft.status,
          pauseUntil: (draft.status === 'paused' || draft.status === 'pending') && draft.pauseUntil ? draft.pauseUntil : null,
          note: draft.note,
          slots: draft.slots,
          restoreSlots: draft.restoreSlots,
          ...(typeof draft.demoBonus === 'boolean' ? { demoBonus: draft.demoBonus } : {}),
        },
        auth()
      );
      const r = res.data;
      setSub(r);
      onStatus?.(r.status);
      setDraft(null);
      const bits = [];
      if (r.released) bits.push(`${r.released} ${r.released === 1 ? 'slot' : 'slots'} freed`);
      if (r.restored?.length) bits.push(`slots given back: ${r.restored.join(', ')}`);
      if (r.notRestored?.length) bits.push(`couldn’t give back: ${r.notRestored.join(', ')}`);
      if (r.emailed) bits.push('login details emailed to the parents');
      if (r.emailError) bits.push(r.emailError);
      setDone(`Saved${bits.length ? ` · ${bits.join(' · ')}` : ''}.`);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Couldn’t save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !sub) return <section className="rmasub rmasp-card" aria-busy="true"><div className="rmasub-skel" /></section>;

  if (loadError && !sub) {
    return (
      <section className="rmasub rmasp-card">
        <p className="rmasub-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {loadError}{' '}
          <button type="button" className="rmasub-link" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </p>
      </section>
    );
  }

  const current = sub.status;
  const freeSaved = sub.savedSlots.filter((s) => s.free);
  const d = draft;
  const leaving = d && d.status !== 'active' && d.status !== 'pending';
  const returning = d && d.status === 'active' && current !== 'active';
  const paying = returning && current === 'pending';
  const states = STATES.filter((s) => s.key !== 'pending' || current === 'pending');

  return (
    <section className={`rmasub rmasp-card ${toneOf(current)}`}>
      <div className="rmasub-head">
        <h2 className="rmasp-h2">
          <FontAwesomeIcon icon={faCalendarCheck} /> Subscription
        </h2>
        <div className="rmasub-seg" role="radiogroup" aria-label="Subscription status">
          {states.map((s) => {
            const on = (d ? d.status : current) === s.key;
            return (
              <button
                key={s.key}
                type="button"
                role="radio"
                aria-checked={on}
                className={`${s.tone} ${on ? 'is-on' : ''}`}
                onClick={() => (s.key === current && !d ? null : s.key === current ? setDraft(null) : startEdit(s.key))}
                disabled={saving}
              >
                <FontAwesomeIcon icon={s.icon} /> {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- Current state ---------- */}
      {!d && (
        <div className="rmasub-now">
          {current === 'active' && (
            <p>
              Taking classes{sub.slots ? ` · weekly slots ${sub.slots}` : ' · no weekly slots picked'}.
            </p>
          )}
          {current === 'pending' && (
            <>
              <p>
                <b>Reserved, not paid yet</b>
                {sub.pauseUntil ? ` · until ${niceDay(sub.pauseUntil)}` : ''}
                {sub.daysLeft != null && sub.daysLeft >= 0 ? ` (${sub.daysLeft} ${sub.daysLeft === 1 ? 'day' : 'days'} left)` : ''}
              </p>
              <p>{sub.slots ? `Weekly slots reserved: ${sub.slots}` : 'No weekly slots yet: pick a teacher and their slots so nobody else takes them.'}</p>
              {sub.enroll && (
                <p>
                  Chose on their link{sub.enroll.at ? ` (${sub.enroll.at})` : ''}: <b>{sub.enroll.packageName || 'a package'}</b>
                  {sub.enroll.amount != null ? ` · $${sub.enroll.amount}` : ''} · pays by <b>{PAY_LABEL[sub.enroll.method] || sub.enroll.method}</b>
                </p>
              )}
              {sub.enroll?.method === 'card' && (
                <p className="rmasub-warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> Send them the card payment link on WhatsApp.
                </p>
              )}
              {sub.note && <p className="rmasub-note">“{sub.note}”</p>}
              {sub.daysLeft != null && sub.daysLeft <= 3 && (
                <p className="rmasub-warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                  {sub.daysLeft < 0 ? 'The reservation has ended.' : 'The reservation ends soon.'} If they don’t pay, the slots are freed and the student is set Stopped.
                </p>
              )}
              <div className="rmasub-actions">
                <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => startEdit('pending')}>
                  <FontAwesomeIcon icon={faPen} /> Change date
                </button>
                <button type="button" className="rmasp-btn" onClick={() => startEdit('active')}>
                  <FontAwesomeIcon icon={faCirclePlay} /> Paid: set active
                </button>
              </div>
              {sub.enroll && !undo && (
                <button type="button" className="rmasub-link rmasub-undo" onClick={() => { setUndoError(''); setUndo({ result: '3' }); }}>
                  <FontAwesomeIcon icon={faRotateLeft} /> Undo this enrollment (a test or a mistake)
                </button>
              )}
              {undo && (
                <div className="rmasub-undo-box">
                  <p>
                    <b>Undo the enrollment from the link?</b> This pending student (login, teacher, reserved times, the “not paid” package) is deleted, and the family’s link
                    is back to “not enrolled”, so they can enroll again.
                  </p>
                  <label>
                    <span>Demo report back to</span>
                    <select className="rmasub-select" value={undo.result} onChange={(e) => setUndo({ result: e.target.value })}>
                      <option value="3">Closed</option>
                      <option value="4">Postponed</option>
                      <option value="5">Not interested</option>
                      <option value="0">Awaiting</option>
                      <option value="">No result</option>
                    </select>
                  </label>
                  {undoError && <p className="rmasub-error">{undoError}</p>}
                  <div className="rmasub-actions">
                    <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setUndo(null)} disabled={undoBusy}>
                      Cancel
                    </button>
                    <button type="button" className="rmasp-btn rmasub-danger" onClick={doUndo} disabled={undoBusy}>
                      <FontAwesomeIcon icon={faRotateLeft} /> {undoBusy ? 'Undoing…' : 'Undo enrollment'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {current !== 'active' && current !== 'pending' && (
            <>
              <p>
                <b>{current === 'paused' ? 'Paused' : 'Stopped'}</b>
                {current === 'paused' && (sub.pauseUntil ? ` until ${niceDay(sub.pauseUntil)}` : ', no return date')}
                {sub.changedAt ? ` · since ${sub.changedAt}` : ''}
              </p>
              <p>
                {sub.slots
                  ? `Slots still reserved: ${sub.slots}`
                  : sub.savedSlots.length
                  ? `Slots freed (remembered: ${sub.savedSlots.map((s) => s.label).join(', ')})`
                  : 'No weekly slots'}
              </p>
              {sub.note && <p className="rmasub-note">“{sub.note}”</p>}
              {current === 'paused' && sub.pauseUntil && sub.pauseUntil <= todayStr() && (
                <p className="rmasub-warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> The return date has passed. Set them Active and add their classes.
                </p>
              )}
              <button type="button" className="rmasub-link" onClick={() => startEdit(current)}>
                <FontAwesomeIcon icon={faPen} /> Edit details
              </button>
            </>
          )}
          {current !== 'active' && sub.upcoming > 0 && (
            <p className="rmasub-warn">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                {sub.upcoming} upcoming {sub.upcoming === 1 ? 'class is' : 'classes are'} still scheduled.{' '}
                {onOpenSchedule && (
                  <button type="button" className="rmasub-link" onClick={onOpenSchedule}>
                    Open Schedule to delete them
                  </button>
                )}
              </span>
            </p>
          )}
          {done && <p className="rmasub-done">{done}</p>}
        </div>
      )}

      {/* ---------- Change ---------- */}
      {d && (
        <div className="rmasub-form">
          {d.status === 'pending' && (
            <>
              <label className="rmasub-field">
                <span>Slots reserved until</span>
                <input type="date" value={d.pauseUntil} min={todayStr()} onChange={(e) => setDraft({ ...d, pauseUntil: e.target.value })} disabled={saving} />
                <small>After this day, if they still haven’t paid, the slots are freed (and remembered).</small>
              </label>
              <label className="rmasub-field">
                <span>Note (optional)</span>
                <input type="text" maxLength={300} placeholder="Pays at the start of the month, …" value={d.note} onChange={(e) => setDraft({ ...d, note: e.target.value })} disabled={saving} />
              </label>
            </>
          )}

          {d.status === 'paused' && (
            <label className="rmasub-field">
              <span>Coming back on (optional)</span>
              <input type="date" value={d.pauseUntil} min={todayStr()} onChange={(e) => setDraft({ ...d, pauseUntil: e.target.value })} disabled={saving} />
              <small>You’ll get a reminder on that day.</small>
            </label>
          )}

          {leaving && (
            <label className="rmasub-field">
              <span>Note (optional)</span>
              <input type="text" maxLength={300} placeholder="Traveling, exams, …" value={d.note} onChange={(e) => setDraft({ ...d, note: e.target.value })} disabled={saving} />
            </label>
          )}

          {leaving && sub.slots && (
            <fieldset className="rmasub-choice">
              <legend>Their weekly slots: {sub.slots}</legend>
              <label className={d.slots === 'keep' ? 'is-on' : ''}>
                <input type="radio" name="rmasub-slots" checked={d.slots === 'keep'} onChange={() => setDraft({ ...d, slots: 'keep' })} disabled={saving} />
                <span>
                  <b>Keep them reserved</b>
                  <small>Nobody else can take these times.</small>
                </span>
              </label>
              <label className={d.slots === 'release' ? 'is-on' : ''}>
                <input type="radio" name="rmasub-slots" checked={d.slots === 'release'} onChange={() => setDraft({ ...d, slots: 'release' })} disabled={saving} />
                <span>
                  <b>Free them for other students</b>
                  <small>They’re remembered, so you can try to give them back later.</small>
                </span>
              </label>
            </fieldset>
          )}

          {returning && sub.savedSlots.length > 0 && (
            <div className="rmasub-restore">
              <label className="rmasub-check">
                <input
                  type="checkbox"
                  checked={d.restoreSlots}
                  onChange={(e) => setDraft({ ...d, restoreSlots: e.target.checked })}
                  disabled={saving || !freeSaved.length}
                />
                <span>Give back their old slots</span>
              </label>
              <ul>
                {sub.savedSlots.map((s) => (
                  <li key={`${s.day}-${s.slot}`} className={s.free ? 'is-good' : 'is-bad'}>
                    {s.label} · {s.free ? 'free' : s.why}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {paying && sub.demoBonus && (
            <label className="rmasub-check">
              <input
                type="checkbox"
                checked={!!d.demoBonus}
                onChange={(e) => setDraft((x) => ({ ...x, demoBonus: e.target.checked }))}
              />
              <span>
                <b>Package bonus for {sub.demoBonus.teacher}</b>
                <small>
                  The teacher who gave the demo gets the new-student bonus (and the “enrolled after your demo” message) once the package is
                  paid. Untick it if they shouldn’t, for example a family coming back months later with an offer.
                </small>
              </span>
            </label>
          )}
          {paying && (
            <p className="rmasub-hint">
              <FontAwesomeIcon icon={faEnvelope} /> The parents get the username and password by email, like any new paid student. Their reserved slots
              stay theirs: add their classes in the Schedule tab.
            </p>
          )}
          {returning && !paying && <p className="rmasub-hint">Then add their classes in the Schedule tab (the slot suggestions will be ready).</p>}

          {leaving && sub.upcoming > 0 && (
            <p className="rmasub-warn">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {sub.upcoming} upcoming {sub.upcoming === 1 ? 'class is' : 'classes are'} still scheduled.
              They won’t be changed; move or delete them in the Schedule tab.
            </p>
          )}

          {saveError && <p className="rmasub-error">{saveError}</p>}

          <div className="rmasub-actions">
            <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setDraft(null)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="rmasp-btn" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : d.status === current ? 'Save details' : paying ? 'Set active & send login' : `Set ${STATES.find((s) => s.key === d.status).label.toLowerCase()}`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default StudentSubscription;
